import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  InfiniteData,
} from "@tanstack/react-query";
import { authenticatedFetch } from "../../../utils/authenticatedFetch";
import PostList from "./PostList";

import { useEffect, useState } from "react";
import { CurrentUser, PaginatedResponse, Post } from "../../../types/types";
import PostSkeleton from "../PostSkeleton";

const CreatedPosts: React.FC = () => {
  const queryClient = useQueryClient();
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const { data: currentUser } = useQuery<CurrentUser>({
    queryKey: ["currentUser"],
    queryFn: () => authenticatedFetch<CurrentUser>("user"),
  });

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery<PaginatedResponse>({
      queryKey: ["posts"],
      queryFn: async ({ pageParam = 1 }) => {
        const raw = await authenticatedFetch<any>(`posts?page=${pageParam}`);

        const mappedPosts: Post[] = raw.data.map((p: any) => ({
          ...p,
          is_liked: p.liked ?? false,
        }));

        return {
          ...raw,
          data: mappedPosts,
        } as PaginatedResponse;
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) =>
        lastPage.current_page < lastPage.last_page
          ? lastPage.current_page + 1
          : undefined,
    });

  const allPosts = data?.pages.flatMap((p) => p.data) ?? [];

  const likeMutation = useMutation({
    mutationFn: (postId: number) =>
      authenticatedFetch(`posts/${postId}/likes`, { method: "POST" }),
    onMutate: async (postId: number) => {
      await queryClient.cancelQueries({ queryKey: ["posts"] });

      const previousData = queryClient.getQueryData<
        InfiniteData<PaginatedResponse>
      >(["posts"]);

      let wasLiked = false;
      if (previousData) {
        outer: for (const page of previousData.pages) {
          for (const p of page.data) {
            if (p.id === postId) {
              wasLiked = p.is_liked ?? false;
              break outer;
            }
          }
        }
      }

      queryClient.setQueryData<InfiniteData<PaginatedResponse>>(
        ["posts"],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map((p) =>
                p.id === postId
                  ? {
                      ...p,
                      likes_count: wasLiked
                        ? p.likes_count - 1
                        : p.likes_count + 1,
                      is_liked: !wasLiked,
                    }
                  : p
              ),
            })),
          };
        }
      );

      return { previousData };
    },
    onError: (err, postId, context) => {
      if (context?.previousData !== undefined) {
        queryClient.setQueryData(["posts"], context.previousData);
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (postId: number) =>
      authenticatedFetch(`posts/${postId}`, { method: "DELETE" }),
    onMutate: (postId) => {
      setDeletingId(postId);
      setTimeout(() => {
        queryClient.setQueryData<InfiniteData<PaginatedResponse>>(
          ["posts"],
          (old) => {
            if (!old) return old;
            return {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                data: page.data.filter((p) => p.id !== postId),
              })),
            };
          }
        );
      }, 300);
    },
    onSettled: () => setDeletingId(null),
  });

  if (isLoading || !currentUser) return <PostSkeleton count={4} />;

  return (
    <PostList
      posts={allPosts}
      currentUser={currentUser}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      onFetchNextPage={() => fetchNextPage()}
      onLike={(id) => likeMutation.mutate(id)}
      onDelete={(id) => deleteMutation.mutate(id)}
      deletingId={deletingId}
    />
  );
};

export default CreatedPosts;
